import { type components, LanguageCode } from '@heidi/schema/ml-scribe';

// The session contract's alias, restated from the schema: /data may not reach a foreign domain.
type SupportedLanguagesResponse = components['schemas']['LanguageView'][];

/** Writing systems excluded from spoken input language selection. */
const INPUT_EXCLUDED_LANGUAGE_CODES = new Set<LanguageCode>([LanguageCode.zh_Hant]);

/** Spoken languages without a formal written standard excluded from output selection. */
const OUTPUT_EXCLUDED_LANGUAGE_CODES = new Set<LanguageCode>([LanguageCode.zh_HK]);

function excludeLegacyKoreanCode(
  languages: SupportedLanguagesResponse
): SupportedLanguagesResponse {
  const hasKoKr = languages.some((language) => language.code === LanguageCode.ko_KR);
  if (!hasKoKr) {
    return languages;
  }

  return languages.filter((language) => language.code !== LanguageCode.ko);
}

export function filterSupportedLanguagesForInput(
  languages: SupportedLanguagesResponse
): SupportedLanguagesResponse {
  return excludeLegacyKoreanCode(
    languages.filter((language) => !INPUT_EXCLUDED_LANGUAGE_CODES.has(language.code))
  );
}

export function filterSupportedLanguagesForOutput(
  languages: SupportedLanguagesResponse
): SupportedLanguagesResponse {
  return excludeLegacyKoreanCode(
    languages.filter((language) => !OUTPUT_EXCLUDED_LANGUAGE_CODES.has(language.code))
  );
}

/**
 * Map a UI display-language pick to a transcription default the backend allows.
 * When the supported list is present (from `/language/supported-languages`), only
 * codes in that list are returned; otherwise the display language is kept.
 */
export function resolveTranscriptionLanguageDefault(
  displayLanguage: LanguageCode | null | undefined,
  supportedTranscriptionLanguageCodes: LanguageCode[] | undefined
): LanguageCode | undefined {
  if (!displayLanguage) {
    return undefined;
  }

  if (!supportedTranscriptionLanguageCodes?.length) {
    return displayLanguage;
  }

  if (supportedTranscriptionLanguageCodes.includes(displayLanguage)) {
    return displayLanguage;
  }

  if (supportedTranscriptionLanguageCodes.includes(LanguageCode.en)) {
    return LanguageCode.en;
  }

  return supportedTranscriptionLanguageCodes[0];
}
